import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';
import { getDesignTokens } from '../utils/designTokens';
import NavBar from '../components/NavBar';

export default function ChangePassword() {
  const { isDarkMode } = useThemeStore();
  const tokens = getDesignTokens(isDarkMode);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const navigate = useNavigate();
  const { changePassword, isLoading, error, clearError } = useAuthStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (newPassword !== confirmPassword) {
      return;
    }

    if (newPassword.length < 8) {
      return;
    }
    
    try {
      await changePassword(currentPassword, newPassword);
      navigate(-1); // Go back to previous page
    } catch (err) {
      // Error is handled by the store
    }
  };

  return (
    <div className={`min-h-screen ${tokens.bg.gradient} flex flex-col relative`}>
      <NavBar className={`${tokens.card.bg} ${tokens.card.shadow} rounded-landing`} containerClassName="px-3 sm:px-4">
        <div className="flex items-center justify-between w-full gap-3 sm:gap-4 min-w-0">
          <h1 className={`text-lg sm:text-xl font-bold ${tokens.text.primary}`}>Runow</h1>
          <button
            onClick={() => navigate(-1)}
            className="px-4 sm:px-6 h-12 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-sm transition-colors flex items-center justify-center flex-shrink-0 text-xs sm:text-sm min-h-[48px]"
          >
            Back
          </button>
        </div>
      </NavBar>

      {/* Main Content */}
      <div className="flex-1 min-h-0 overflow-y-auto scrollable">
        <div className="min-h-full flex items-start sm:items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-sm shadow-lg p-6 sm:p-8">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 text-center">
            Change Password
          </h2>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-sm p-4">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Current Password
              </label>
              <input
                id="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent touch-target"
                placeholder="Enter current password"
              />
            </div>

            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent touch-target"
                placeholder="Enter new password"
              />
              <p className="text-xs text-gray-500 mt-1">Must be at least 8 characters</p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Confirm New Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent touch-target"
                placeholder="Confirm new password"
              />
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
              )}
            </div>

            <div className="flex gap-4 pt-2">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-sm transition-colors touch-target min-h-[48px]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading || newPassword !== confirmPassword || newPassword.length < 8 || !currentPassword}
                className={`flex-1 ${tokens.button.primary} disabled:opacity-50 disabled:cursor-not-allowed ${tokens.text.inverse} font-semibold py-3 px-6 rounded-landing transition-colors touch-target min-h-[48px]`}
              >
                {isLoading ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </form>
          </div>
        </div>
      </div>
    </div>
  );
}
